import { useMemo, useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';
import {
  pushUserToClientMessage,
  pushUserToWebsocketMessage,
  selectCurrentChatInput,
  selectIsActorWaitingForResponse,
  setInputWithClient,
} from '../../../store/chat/chatSlice';
import {
  selectCurrentActor,
  selectCurrentClient,
} from '../../../store/client/clientSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import ChatInputBase from './ChatInputBase';
import ChatInputPlaceholder from './ChatInputPlaceholder';

export interface ChatInputClientProps {
  selfPlayCard: JSX.Element | undefined;
}

export default function ChatInputClient({
  selfPlayCard,
}: ChatInputClientProps) {
  const dispatch = useAppDispatch();
  const currentClient = useAppSelector(selectCurrentClient);
  const currentActor = useAppSelector(selectCurrentActor);
  const currentChatInput = useAppSelector(selectCurrentChatInput);
  const isActorWaitingForResponse = useAppSelector(
    selectIsActorWaitingForResponse
  );
  const [textKey, setTextKey] = useState(uuidv4());

  // Disable input if in actor chat and waiting for actor response
  const isInputDisabled = !!currentActor && isActorWaitingForResponse;

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const textValue = useMemo(
    () => currentChatInput?.inputWithClient || '',
    [currentChatInput]
  );

  const cycleTextKey = () => {
    const key = textKey;
    setTextKey(uuidv4());
    return key;
  };

  const handleInputChange = useCallback(
    (payload: string) => {
      const userId = currentClient?.userId;
      if (userId) {
        dispatch(setInputWithClient({ userId, payload }));
      }
    },
    [currentClient?.userId, dispatch]
  );

  useEffect(() => {
    if (transcript) {
      handleInputChange(transcript);
    }
  }, [handleInputChange, transcript]);

  const handleListenToggle = useCallback(() => {
    if (listening) {
      SpeechRecognition.stopListening();
    } else {
      // Clear previous transcript
      SpeechRecognition.startListening({ continuous: true });
    }
  }, [listening]);

  const handleInputSubmit = (payload: string) => {
    const userId = currentClient?.userId;
    if (!userId) {
      return;
    }
    dispatch(
      pushUserToClientMessage({ userId, payload, messageId: cycleTextKey() })
    );
    if (currentClient.userType === 'client' && !currentActor) {
      dispatch(pushUserToWebsocketMessage({ userId, payload }));
    }
    // (Optional) Stop listening and reset when message is sent
    SpeechRecognition.stopListening();
    resetTranscript();
  };

  return currentClient?.isArchived ? (
    <ChatInputPlaceholder text="Chat archived." />
  ) : (
    <ChatInputBase
      textKey={textKey}
      textValue={textValue}
      onTextValueChange={handleInputChange}
      onTextValueSubmit={handleInputSubmit}
      isListening={listening}
      onListenToggle={handleListenToggle}
      browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
      submitButtonProps={{
        className: 'bg-gradient-to-br from-blue-500 to-purple-500',
      }}
      textAreaProps={{ placeholder: 'Message to client' }}
      selfPlayCard={selfPlayCard}
      view="client-chat"
      isInputDisabled={isInputDisabled}
    />
  );
}
